import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { HttpClient, HttpClientModule } from '@angular/common/http';
import { FormSignatureComponent } from '../../items/form-signature.component';
import { FormDataCardComponent, FormConfig, FormDataRecord } from '../../form-data-card/form-data-card.component';
import { FormDataService } from '../../../services/form-data.service';
import { environment } from '../../../services/global-environment';

// 承攬商介面
interface Contractor {
  id: string;
  companyName: string;
  uniformNumber: string;
  responsiblePerson: string;
  contactPhone: string;
  safetyOfficer: string;
  safetyOfficerPhone: string;
}

// 所需資料項目介面
interface RequiredDocument {
  id: string;
  name: string;
  status: 'not_filled' | 'filled' | 'completed';
  required: boolean;
  workTypes?: string[]; // 對應的作業類別（可選）
  specialWorkTypes?: string[]; // 對應的特殊作業（可選）
}

// 承攬商員工介面
interface ContractorEmployee {
  id: string;
  name: string;
  idNumber: string;
  phone: string;
  contractorId: string;
}

// 區塊收合狀態介面
interface PanelCollapseState {
  basicInfo: boolean;
  workContent: boolean;
  personnelSelection: boolean;
  requiredDocuments: boolean;
  signatureArea: boolean;
}

@Component({
  selector: 'app-work-application-form',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    HttpClientModule,
    FormSignatureComponent,
    FormDataCardComponent
  ],
  templateUrl: './work-application-form.component.html',
  styleUrl: './work-application-form.component.scss'
})
export class WorkApplicationFormComponent implements OnInit {
  @Input() isEditMode: boolean = false;
  @Input() initialData: any = null;
  @Output() formSubmit = new EventEmitter<any>();
  @Output() formCancel = new EventEmitter<void>();

  // 表單
  workApplicationForm!: FormGroup;
  powerRequestForm!: FormGroup;

  // 承攬商相關
  contractors: Contractor[] = [];
  selectedContractor: Contractor | null = null;
  isLoadingContractors: boolean = false;

  // 人員選擇相關
  availablePersonnel: ContractorEmployee[] = [];
  selectedPersonnel: ContractorEmployee[] = [];
  personnelSearchTerm: string = '';
  isLoadingPersonnel: boolean = false;

  // 區塊展開/收合狀態
  panelStates: PanelCollapseState = {
    basicInfo: true,
    workContent: true,
    personnelSelection: true,
    requiredDocuments: true,
    signatureArea: true
  };

  // 所需資料列表
  requiredDocuments: RequiredDocument[] = [];
  filteredRequiredDocuments: RequiredDocument[] = [];

  // 新增：表單資料配置
  formDataConfigs: FormConfig[] = [];

  // 作業類別選項
  workTypes = [
    { id: 'workType1', label: '1.用電作業' },
    { id: 'workType2', label: '2.吊掛、搬運等作業' },
    { id: 'workType3', label: '3.儀器設備' },
    { id: 'workType4', label: '4.電器工程' },
    { id: 'workType5', label: '5.高空作業' },
    { id: 'workType6', label: '6.管線拆卸、安裝、檢修等工程' },
    { id: 'workType7', label: '7.塔槽之修理清繕、維護等工程' },
    { id: 'workType8', label: '8.土木拆卸、挖掘孔洞、穿牆打孔、室內裝修及新建工程' },
    { id: 'workType9', label: '9.有機溶劑' },
    { id: 'workType10', label: '10.接觸危害物質或粉塵作業' },
    { id: 'workType11', label: '11.狹小空間、局限場所、涵洞等工程' },
    { id: 'workType12', label: '12.消防工程' },
    { id: 'workType13', label: '13.空調管線工程' },
    { id: 'workType14', label: '14.其他' }
  ];

  constructor(
    private fb: FormBuilder,
    private http: HttpClient,
    private formDataService: FormDataService
  ) {}

  ngOnInit(): void {
    this.initWorkApplicationForm();
    this.initPowerRequestForm();
    this.loadContractorList();
    this.initRequiredDocuments();
    this.updateRequiredDocuments(); // 確保初始化後就正確設定表單資料配置

    if (this.initialData) {
      this.loadFormData(this.initialData);
    }
  }

  // 初始化基本表單
  initWorkApplicationForm(): void {
    const today = new Date();

    this.workApplicationForm = this.fb.group({
      // 基本資訊區塊
      year: [today.getFullYear(), [Validators.required]],
      month: [today.getMonth() + 1, [Validators.required]],
      day: [today.getDate(), [Validators.required]],
      workName: ['', Validators.required], // 新增：作業名稱
      formNumber: [{value: this.generateFormNumber(), disabled: true}], // 自動生成表單編號
      uniformNumber: ['', Validators.required],
      ehsElectrician: [{value: '預設電工', disabled: true}],
      requestDepartment: ['', Validators.required],
      requestContactInfo: ['', Validators.required],
      contractorId: ['', Validators.required], // 選擇承攬商

      // 作業時間
      startYear: [today.getFullYear(), [Validators.required]],
      startMonth: [today.getMonth() + 1, [Validators.required]],
      startDay: [today.getDate(), [Validators.required]],
      startHour: [9, [Validators.required]],
      endYear: [today.getFullYear(), [Validators.required]],
      endMonth: [today.getMonth() + 1, [Validators.required]],
      endDay: [today.getDate() + 1, [Validators.required]],
      endHour: [17, [Validators.required]],

      // 作業內容區塊
      workLocation: ['', Validators.required],
      contractingUnit: ['', Validators.required], // 承攬單位
      workContent: ['', Validators.required],

      // 作業類別
      workType1: [false],
      workType2: [false],
      workType3: [false],
      workType4: [false],
      workType5: [false],
      workType6: [false],
      workType7: [false],
      workType8: [false],
      workType9: [false],
      workType10: [false],
      workType11: [false],
      workType12: [false],
      workType13: [false],
      workType14: [false],
      otherWorkContent: [''],

      // 特殊作業
      hotWork: [null], // 動火作業
      suspendedWork: [null], // 吊掛作業
      confinedSpaceWork: [null], // 局限空間作業
      organicSolventWork: [null], // 有機溶劑作業
      highAltitudeWork: [null], // 高架作業
      specialWork: [null], // 特殊作業
      electricalWork: [null], // 電器作業
      powerRequirements: [''], // 用電設備需求功率
      voltage: [''], // 電壓
      phase: [''], // 相
      ampere: [''], // 安培
      needEngineer: [null], // 廠務工程部人員協同監工

      // 使用工具
      tool1: [false], // 電焊
      tool2: [false], // 電動機
      tool3: [false], // 電熱器
      tool4: [false], // 電鑽
      tool5: [false], // 噴漆
      tool6: [false], // 氣氛
      tool7: [false], // 砂輪機
      tool8: [false], // 塑膠焊
      tool9: [false], // 其他
      otherTools: [''], // 其他工具說明

      // 工作內容
      workContentType1: [false], // 拆(配)線
      workContentType2: [false], // 新裝/拆除
      workContentType3: [false], // 配管
      workContentType4: [false], // 維修保養
      workContentType5: [false], // 油漆/防水
      workContentType6: [false], // 清潔
      workContentType7: [false], // 消防
      workContentType8: [false], // 其他
      otherWorkContentType: [''], // 其他工作內容說明

      // 簽核區域
      powerConfigType: [''], // 電源配置方式
      engineerSignature: [''], // 機房責任工程師簽章
      safetyOfficerSignature: [''], // 環安衛生管理人員簽章
      constructionSuggestions: [''], // 施工部門現場施工期間建議事項
      constructionStaffSignature: [''], // 施工部門人員簽章

      // 主承攬商簽章區域
      contractorResponsiblePerson: [''], // 主承攬商現場負責人
      contractorSafetyOfficer: [''], // 主承攬商工安人員

      // 電器允許方式
      electricalPermissionType1: [false], // 進行___樓層停電
      electricalFloorNumber: [''], // 停電樓層數
      electricalPermissionType2: [false], // 需配專線
      electricalPermissionType3: [false], // 指定不斷電系統插座
      electricalPermissionType4: [false], // 一般插座___V
      electricalVoltage: [''], // 電壓值
      electricalPermissionType5: [false], // 指定電器箱
      electricalPermissionType6: [false], // 其他
      electricalOtherDescription: [''], // 其他說明

      // 廠務工程部
      engineeringDeptSuggestions: [''], // 廠務工程部建議意見
      engineeringDeptStaff: [''], // 廠務工程部人員簽章
      engineeringDeptManager: [''], // 總務或廠務工程部主管簽章
      engineeringDeptSupervisor: [''], // 廠務工程部監工人員簽章

      // 環安衛生安部門
      ehsDeptSuggestions: [''], // 環安衛生安部門建議意見
      ehsDeptStaff: [''], // 環安衛生安部人員簽章
      ehsDeptManager: [''], // 環安衛生安部主管簽章

      // 請購施工部門
      constructionDeptSuggestions: [''], // 請購施工部門現場施工期間建議意見
      constructionDeptStaff: [''], // 施工部門人員簽章
      constructionDeptManager: [''], // 施工部門主管簽章
      constructionDeptSupervisor: [''], // 部門現場監工人員簽章

      // 最終簽章（完工後）
      finalEhsDeptStaff: [''], // 環安衛生安部人員最終簽章
      finalEhsDeptManager: [''], // 環安衛生安部主管最終簽章
    });

    // 監聽作業類別變化以更新所需資料
    this.workTypes.forEach(workType => {
      this.workApplicationForm.get(workType.id)?.valueChanges.subscribe(() => {
        this.updateRequiredDocuments();
      });
    });

    // 監聽特殊作業變化以更新所需資料
    const specialWorkControls = [
      'hotWork', 'suspendedWork', 'confinedSpaceWork', 'organicSolventWork',
      'electricalWork', 'highAltitudeWork', 'specialWork'
    ];

    specialWorkControls.forEach(controlName => {
      this.workApplicationForm.get(controlName)?.valueChanges.subscribe(() => {
        this.updateRequiredDocuments();
      });
    });

    // 監聽承攬商選擇變化
    this.workApplicationForm.get('contractorId')?.valueChanges.subscribe(contractorId => {
      this.onContractorChange(contractorId);
      // 當承攬商改變時載入該承攬商的員工資料
      if (contractorId) {
        this.loadContractorPersonnel(contractorId);
      } else {
        this.availablePersonnel = [];
        this.selectedPersonnel = [];
      }
    });
  }

  // 初始化電源申請表單
  initPowerRequestForm(): void {
    this.powerRequestForm = this.fb.group({
      powerType1: [false],
      powerType2: [false],
      powerType3: [false],
      powerType4: [false],
      powerType5: [false],
      powerType6: [false],
      otherPowerType: ['']
    });
  }

  // 生成表單編號
  generateFormNumber(): string {
    const today = new Date();
    const year = today.getFullYear();
    const month = (today.getMonth() + 1).toString().padStart(2, '0');
    const day = today.getDate().toString().padStart(2, '0');
    const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    return `WA${year}${month}${day}${random}`;
  }

  // 載入承攬商列表
  loadContractorList(): void {
    this.isLoadingContractors = true;
    const companyid = environment.clientId;
    const apiUrl = `${environment.apiUrl}/api/contractor/list`;

    console.log('Loading contractor list from API...');

    this.http.get<any>(apiUrl, {
      params: { companyid: companyid }
    }).subscribe({
      next: (response) => {
        this.isLoadingContractors = false;
        if (response.success && response.data) {
          console.log('承攬商列表載入成功:', response.data);

          this.contractors = response.data.map((item: any) => ({
            id: item.id,
            companyName: item.companyName,
            uniformNumber: item.uniformNumber,
            responsiblePerson: item.responsiblePerson,
            contactPhone: item.contactPhone,
            safetyOfficer: item.safetyOfficer,
            safetyOfficerPhone: item.safetyOfficerPhone
          }));
        } else {
          console.error('API 回傳錯誤:', response);
          this.loadMockContractors();
        }
      },
      error: (error) => {
        this.isLoadingContractors = false;
        console.error('載入承攬商列表失敗:', error);
        this.loadMockContractors();
      }
    });
  }

  // 載入模擬承攬商資料
  loadMockContractors(): void {
    this.contractors = [
      {
        id: '1',
        companyName: '永鴻營造股份有限公司',
        uniformNumber: '12345678',
        responsiblePerson: '張三',
        contactPhone: '02-12345678',
        safetyOfficer: '李四',
        safetyOfficerPhone: '0912345678'
      },
      {
        id: '2',
        companyName: '建成工程有限公司',
        uniformNumber: '87654321',
        responsiblePerson: '王五',
        contactPhone: '02-87654321',
        safetyOfficer: '趙六',
        safetyOfficerPhone: '0987654321'
      }
    ];
  }

  // 承攬商變更處理
  onContractorChange(contractorId: string): void {
    this.selectedContractor = this.contractors.find(c => c.id === contractorId) || null;
    if (this.selectedContractor) {
      // 自動填入承攬商相關資訊
      this.workApplicationForm.patchValue({
        uniformNumber: this.selectedContractor.uniformNumber,
        contractingUnit: this.selectedContractor.companyName
      });

      // 載入承攬商員工資料
      this.loadContractorPersonnel(contractorId);
    } else {
      // 清空人員資料
      this.availablePersonnel = [];
      this.selectedPersonnel = [];
    }
  }

  // 承攬商選擇變更事件處理
  onContractorSelectChange(event: Event): void {
    const target = event.target as HTMLSelectElement;
    const contractorId = target.value;
    this.onContractorChange(contractorId);
  }

  // 初始化所需資料列表
  initRequiredDocuments(): void {
    this.requiredDocuments = [
      {
        id: '6d755ec1-5f87-4f66-b6f1-14f0e3167624',
        name: '工作場所環境及危害因素告知單',
        status: 'not_filled',
        required: true // 永遠顯示
      },
      // 特殊作業相關文件
      {
        id: '125f6568-d76d-4886-8211-bf72345dddba',
        name: '動火作業-許可證',
        status: 'not_filled',
        required: false,
        specialWorkTypes: ['hotWork']
      },
      {
        id: '5513327b-df14-4eca-b347-ec67554538fc',
        name: '吊掛作業-許可證',
        status: 'not_filled',
        required: false,
        specialWorkTypes: ['suspendedWork']
      },
      {
        id: 'a22f011b-9155-4e8e-8744-871ef4ac5986',
        name: '局限空間作業-許可證',
        status: 'not_filled',
        required: false,
        specialWorkTypes: ['confinedSpaceWork']
      },
      {
        id: '0e0d7aec-22d2-4bf0-899d-f90c9d14ea08',
        name: '有機溶劑作業-許可證',
        status: 'not_filled',
        required: false,
        specialWorkTypes: ['organicSolventWork']
      },
      {
        id: 'eaa426ec-ff8b-4681-bcdb-b7fcd55e49b2',
        name: '高架作業-許可證',
        status: 'not_filled',
        required: false,
        specialWorkTypes: ['highAltitudeWork']
      },
      {
        id: '1f5b1ca4-776e-41b9-99ff-50a38e9ec1a5',
        name: '特殊作業-許可證',
        status: 'not_filled',
        required: false,
        specialWorkTypes: ['specialWork']
      }
    ];

    this.updateRequiredDocuments();
  }

  // 更新所需資料列表
  updateRequiredDocuments(): void {
    const selectedWorkTypes = this.workTypes
      .filter(workType => this.workApplicationForm.get(workType.id)?.value)
      .map(workType => workType.id);

    // 獲取選擇的特殊作業
    const selectedSpecialWorks: string[] = [];
    const specialWorkControls = [
      'hotWork', 'suspendedWork', 'confinedSpaceWork', 'organicSolventWork',
      'electricalWork', 'highAltitudeWork', 'specialWork'
    ];

    specialWorkControls.forEach(controlName => {
      if (this.workApplicationForm.get(controlName)?.value) {
        selectedSpecialWorks.push(controlName);
      }
    });

    this.filteredRequiredDocuments = this.requiredDocuments.filter(doc => {
      // 必要文件總是顯示
      if (doc.required) {
        return true;
      }

      // 條件性文件依據選擇的特殊作業顯示
      if (doc.specialWorkTypes && doc.specialWorkTypes.length > 0) {
        return doc.specialWorkTypes.some(specialWork => selectedSpecialWorks.includes(specialWork));
      }

      // 條件性文件依據選擇的作業類別顯示
      if (doc.workTypes && doc.workTypes.length > 0) {
        return doc.workTypes.some(workType => selectedWorkTypes.includes(workType));
      }

      return false;
    });

    // 同時更新 formDataConfigs，只顯示過濾後需要的表單
    const allFormConfigs = this.formDataService.getFormConfigs();
    this.formDataConfigs = allFormConfigs.filter(config => {
      return this.filteredRequiredDocuments.some(doc => doc.id === config.formid);
    });
  }

  // 處理文件填寫
  handleDocumentFill(docId: string): void {
    const document = this.requiredDocuments.find(doc => doc.id === docId);
    if (document) {
      // 這裡可以開啟對應的填寫頁面或模態框
      console.log('開啟文件填寫:', document.name);
      // 暫時標記為已填寫
      document.status = 'filled';
    }
  }

  // 載入表單資料（編輯模式）
  loadFormData(data: any): void {
    if (data) {
      this.workApplicationForm.patchValue(data);
    }
  }

  // 表單提交
  onSubmit(): void {
    if (this.workApplicationForm.invalid) {
      this.markFormGroupTouched(this.workApplicationForm);
      return;
    }

    const formData = {
      ...this.workApplicationForm.getRawValue(),
      powerRequest: this.powerRequestForm.value,
      requiredDocuments: this.filteredRequiredDocuments,
      selectedPersonnel: this.selectedPersonnel // 加入選擇的人員資料
    };

    this.formSubmit.emit(formData);
  }

  // 取消
  onCancel(): void {
    this.formCancel.emit();
  }

  // 標記表單為已觸摸
  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsTouched();
      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }

  // 獲取文件狀態的顯示文字
  getDocumentStatusText(status: string): string {
    switch (status) {
      case 'not_filled': return '未填寫';
      case 'filled': return '已填寫';
      case 'completed': return '已完成';
      default: return '未知';
    }
  }

  // 獲取文件狀態的CSS類別
  getDocumentStatusClass(status: string): string {
    switch (status) {
      case 'not_filled': return 'status-not-filled';
      case 'filled': return 'status-filled';
      case 'completed': return 'status-completed';
      default: return '';
    }
  }

  // 切換區塊展開/收合狀態
  togglePanel(panelName: keyof PanelCollapseState): void {
    this.panelStates[panelName] = !this.panelStates[panelName];
  }

  // 載入承攬商員工資料
  loadContractorPersonnel(contractorId: string): void {
    this.isLoadingPersonnel = true;
    this.availablePersonnel = [];
    this.selectedPersonnel = [];

    const companyid = environment.clientId;
    const apiUrl = `${environment.apiUrl}/api/contractor/employee/list`;

    console.log('Loading contractor personnel...', { companyid, contractorId });

    this.http.get<any>(apiUrl, {
      params: {
        companyid: companyid,
        contractorId: contractorId
      }
    }).subscribe({
      next: (response) => {
        this.isLoadingPersonnel = false;
        if (response.success && response.data) {
          console.log('承攬商員工資料載入成功:', response.data);

          this.availablePersonnel = response.data.map((employee: any) => ({
            id: employee.id,
            name: employee.name,
            idNumber: employee.idNumber,
            phone: employee.phone,
            contractorId: employee.contractorId
          }));
        } else {
          console.error('API 回傳錯誤:', response);
          this.loadMockPersonnel();
        }
      },
      error: (error) => {
        this.isLoadingPersonnel = false;
        console.error('載入承攬商員工資料失敗:', error);
        this.loadMockPersonnel();
      }
    });
  }

  // 載入模擬員工資料
  loadMockPersonnel(): void {
    this.availablePersonnel = [
      {
        id: '1',
        name: '王小明',
        idNumber: 'A123456789',
        phone: '0912345678',
        contractorId: this.workApplicationForm.get('contractorId')?.value || ''
      },
      {
        id: '2',
        name: '李小華',
        idNumber: 'B987654321',
        phone: '0987654321',
        contractorId: this.workApplicationForm.get('contractorId')?.value || ''
      },
      {
        id: '3',
        name: '張小美',
        idNumber: 'C456789123',
        phone: '0956789123',
        contractorId: this.workApplicationForm.get('contractorId')?.value || ''
      }
    ];
  }

  // 取得過濾後的可選人員列表
  get filteredAvailablePersonnel(): ContractorEmployee[] {
    if (!this.personnelSearchTerm.trim()) {
      return this.availablePersonnel;
    }

    const searchTerm = this.personnelSearchTerm.toLowerCase();
    return this.availablePersonnel.filter(person =>
      person.name.toLowerCase().includes(searchTerm) ||
      person.idNumber.toLowerCase().includes(searchTerm) ||
      person.phone.includes(searchTerm)
    );
  }

  // 選擇人員
  selectPerson(person: ContractorEmployee): void {
    // 從可選列表中移除
    const index = this.availablePersonnel.findIndex(p => p.id === person.id);
    if (index > -1) {
      this.availablePersonnel.splice(index, 1);
      // 加入已選列表
      this.selectedPersonnel.push(person);
    }
  }

  // 移除人員
  removePerson(person: ContractorEmployee): void {
    // 從已選列表中移除
    const index = this.selectedPersonnel.findIndex(p => p.id === person.id);
    if (index > -1) {
      this.selectedPersonnel.splice(index, 1);
      // 加入可選列表
      this.availablePersonnel.push(person);
    }
  }

  // 全選人員
  selectAllPersonnel(): void {
    // 複製當前過濾結果，避免在遍歷過程中修改陣列
    const filtered = [...this.filteredAvailablePersonnel];

    filtered.forEach(person => {
      const index = this.availablePersonnel.findIndex(p => p.id === person.id);
      if (index > -1) {
        this.availablePersonnel.splice(index, 1);
        this.selectedPersonnel.push(person);
      }
    });
  }

  // 清空所有已選人員
  clearAllSelected(): void {
    this.availablePersonnel.push(...this.selectedPersonnel);
    this.selectedPersonnel = [];
  }

  // 執行人員搜尋
  searchPersonnel(): void {
    // 搜尋功能已經通過 filteredAvailablePersonnel getter 實現
    // 這個方法主要是為了搜尋按鈕的點擊事件
    console.log('執行搜尋:', this.personnelSearchTerm);
  }

  // 清空搜尋條件
  clearSearch(): void {
    this.personnelSearchTerm = '';
  }

  // 新增：初始化表單資料配置
  // 新增：處理新增表單資料
  onAddFormData(formName: string): void {
    // 這裡可以開啟動態表單對話框
    // 暫時使用簡單的提示
    console.log('開啟表單:', formName);
    // TODO: 整合動態表單元件
    alert(`開啟表單：${formName}\n\n(此功能將在後續整合動態表單元件時實作)`);
  }

  // 新增：處理查看表單資料列表
  onViewFormDataList(formName: string): void {
    console.log('查看列表:', formName);
    this.formDataService.getFormRecords(formName).subscribe({
      next: (records: FormDataRecord[]) => {
        const recordInfo = records.length > 0
          ? records.map(r => `- ${JSON.stringify(r.data)}`).join('\n')
          : '無資料';
        alert(`表單 ${formName} 共有 ${records.length} 筆資料\n\n${recordInfo}`);
      },
      error: (error: any) => {
        console.error('載入表單資料失敗:', error);
        alert(`載入表單 ${formName} 資料時發生錯誤`);
      }
    });
  }

  // 新增：處理查看表單資料詳情
  onViewFormDataDetail(event: {formName: string, recordId: string}): void {
    console.log('查看詳情:', event);
    const record = this.formDataService.getFormRecord(event.recordId);
    if (record) {
      alert(`資料詳情：\n${JSON.stringify(record.data, null, 2)}`);
    } else {
      alert('找不到指定的資料記錄');
    }
  }
}
